import React, { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { toast } from '../../ui/toast';
import { AUTH_KEYS, NS } from '../../i18n/constants/keys';
import { TextInput } from '../../ui/input/text_input';
import { RootState } from '../../utils/redux_store/store';
import { FormInput } from '../../utils/types/login_register_types';
import { AuthenticationUiLayout } from '../login_register/AuthenticationUILayout';
import { CompanyLogoBadge } from '../login_register/CompanyLogoBadge';
import { validate, validateForm } from '../login_register/validation/validation_rules';
import { acceptInvite, getInvite, InviteDetails } from '../../utils/api_service/invite_api';
import { AnimatedLoginSequence } from '../../assets/svgs/AnimateLogin';
import { apiUrl } from '../../utils/api_service/api_url';
import { finishSignIn } from '../../utils/auth/finishSignIn';
import { setPending } from '../../utils/auth/mfaHandoff';
import { ProviderIcon } from '../login_register/ProviderIcon';

type Stage = 'loading' | 'ready' | 'invalid';

export const InvitePage = () => {
  const history = useHistory();
  const { t } = useTranslation(NS.auth);
  const { token } = useParams<{ token: string }>();
  const [stage, setStage] = useState<Stage>('loading');
  const [details, setDetails] = useState<InviteDetails | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState<FormInput>({
    email: '',
    password: '',
    confirmPassword: '',
    errors: { emailError: '', passwordError: '', confirmPasswordError: '' },
  });

  useEffect(() => {
    getInvite(token)
      .then((res) => res.json())
      .then((data: InviteDetails) => {
        if (data && data.success) {
          setDetails(data);
          setForm((f) => ({ ...f, email: data.email }));
          setStage('ready');
        } else {
          setStage('invalid');
        }
      })
      .catch(() => setStage('invalid'));
  }, [token]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const errors = validate(event.target, form);
    setForm({ ...form, [event.target.name]: event.target.value, errors });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validateForm(form)) {
      toast.error(t(AUTH_KEYS.login.invalidDetails), { position: 'bottom-center' });
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await acceptInvite(token, form.password);
      const data = await res.json();
      if (!res.ok || !data.success) {
        setStage('invalid');
        toast.error(t(AUTH_KEYS.login.invalidDetails), { position: 'bottom-center' });
        return;
      }
      // The invitation's deep-link (e.g. the specific assessment) wins,
      // otherwise route by role like a normal sign-in.
      const next = data.nextPath || details?.nextPath || '';
      // Same MFA gates as password login: an enrolled authenticator is
      // challenged, a tenant policy forces enrolment, before any session.
      if (data.mfaRequired && data.challengeToken) {
        setPending('challenge', data.challengeToken, next);
        history.push('/auth/mfa');
        return;
      }
      if (data.mfaEnrollmentRequired && data.enrollToken) {
        setPending('enroll', data.enrollToken, next);
        history.push('/auth/mfa/enroll');
        return;
      }
      // One seam for "signed in".
      history.push(await finishSignIn(data, { deepLink: next }));
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Something went wrong';
      toast.error(errorMessage, { position: 'bottom-center' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const companyLogo = useSelector((state: RootState) => state.logo.companyLogo);
  const companyName = useSelector((state: RootState) => state.logo.companyName);

  return (
    <AuthenticationUiLayout leftContent={<AnimatedLoginSequence />}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          width: '100%',
          maxWidth: 440,
          gap: 3.5,
          pt: { xs: 4, sm: 6 },
          mx: 'auto',
        }}
      >
        <CompanyLogoBadge logoUrl={companyLogo} companyName={companyName} />

        {stage === 'loading' && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              py: 6,
              width: '100%',
            }}
          >
            <CircularProgress size={24} sx={{ color: 'primary.main' }} />
            <Typography variant="body2" color="text.secondary">
              Loading your invitation…
            </Typography>
          </Box>
        )}

        {stage === 'invalid' && (
          <Paper
            elevation={0}
            sx={{
              p: 3.5,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              width: '100%',
            }}
          >
            <Typography variant="h2" sx={{ fontSize: '1.25rem', fontWeight: 700, color: 'text.primary' }}>
              This invitation link is no longer valid
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              It may have already been used or expired. Ask your administrator to send a new invitation.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => history.push('/login')}
              sx={{
                borderRadius: 9999,
                alignSelf: 'flex-start',
                mt: 1,
                px: 3,
                py: 1,
                fontWeight: 600,
              }}
            >
              Go to sign in
            </Button>
          </Paper>
        )}

        {stage === 'ready' && details && (
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              gap: 2.5,
            }}
          >
            <Box>
              <Typography
                variant="h1"
                sx={{
                  fontSize: '1.625rem',
                  fontWeight: 700,
                  color: 'text.primary',
                  letterSpacing: '-0.02em',
                  mb: 0.75,
                }}
              >
                Welcome{details.firstName ? `, ${details.firstName}` : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Set a password to join {details.organizationName || 'your organization'}.
              </Typography>
            </Box>

            <TextInput
              type="email"
              label={t(AUTH_KEYS.register.email)}
              name="email"
              value={form.email}
              disabled
              onChange={handleChange}
            />

            <TextInput
              type="password"
              label={t(AUTH_KEYS.register.password)}
              name="password"
              value={form.password}
              onChange={handleChange}
              error={Boolean(form.errors.passwordError)}
              helperText={form.errors.passwordError}
              required
            />

            <TextInput
              type="password"
              label={t(AUTH_KEYS.register.confirmPassword)}
              name="confirmPassword"
              value={form.confirmPassword || ''}
              onChange={handleChange}
              error={Boolean(form.errors.confirmPasswordError)}
              helperText={form.errors.confirmPasswordError}
              required
            />

            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              disabled={isSubmitting}
              sx={{
                borderRadius: 9999,
                py: 1.25,
                mt: 1,
                fontWeight: 600,
                fontSize: '0.875rem',
              }}
            >
              {isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Create account'
              )}
            </Button>
          </Box>
        )}

        {stage === 'ready' && (details?.methods?.ssoOptions?.length || 0) > 0 && (
          <Stack spacing={1.5} sx={{ width: '100%' }}>
            <Divider sx={{ my: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                OR
              </Typography>
            </Divider>
            {details!.methods.ssoOptions!.map((opt) => (
              <Button
                key={opt.provider}
                type="button"
                variant="outlined"
                fullWidth
                onClick={() => {
                  const returnTo = encodeURIComponent(window.location.origin);
                  window.location.href = `${apiUrl}/auth/sso/start?returnTo=${returnTo}&provider=${encodeURIComponent(
                    opt.provider
                  )}&invite=${encodeURIComponent(token)}`;
                }}
                startIcon={<ProviderIcon provider={opt.provider} />}
                sx={{
                  borderRadius: 9999,
                  py: 1.2,
                  px: 2.5,
                  borderColor: '#e4e4e7',
                  color: 'text.primary',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  backgroundColor: '#ffffff',
                  '&:hover': {
                    backgroundColor: '#f4f4f5',
                    borderColor: '#d4d4d8',
                  },
                }}
              >
                {opt.label || 'Continue with SSO'}
              </Button>
            ))}
          </Stack>
        )}
      </Box>
    </AuthenticationUiLayout>
  );
};
